import { CardSubmitResult } from 'components/Alpha/cards/cardTypes';
import { ALPHA_NO_RESPONSE_MESSAGE, AlphaEvent } from 'services/alpha/types';

export const SUBMIT_UNFINISHED_MESSAGE = "Alpha didn't finish that. Try again.";
const RATE_LIMITED_MESSAGE = "You're sending messages quickly.";
const RETRY_LATER = 'Wait a moment, then try again.';
export const TURN_IN_PROGRESS_MESSAGE =
  'Alpha is still working on your last message. Try again in a moment.';

type EventType = AlphaEvent['type'];
type CardRequiredEvent = Extract<AlphaEvent, { type: 'card_required' }>;

const PROGRESS_TYPES: EventType[] = [
  'text_delta',
  'tool_call',
  'tool_result',
  'confirmation_required',
  'visual',
];
const TURN_TYPES: EventType[] = [
  ...PROGRESS_TYPES,
  'card_required',
  'card_error',
  'done',
  'error',
  'timeout',
];

export const isTurnProgress = (event: AlphaEvent): boolean =>
  PROGRESS_TYPES.includes(event.type) || event.type === 'card_required';

export type SubmitEffect =
  | { type: 'forward'; event: AlphaEvent }
  | { type: 'replaceCard'; event: CardRequiredEvent }
  | { type: 'closeCard' }
  | { type: 'startTurn' }
  | { type: 'rateLimited'; message?: string; retryAfterSeconds: number }
  | { type: 'turnInProgress' };

export interface SubmitState {
  token: string;
  started: boolean;
  unreachable: boolean;
  result?: CardSubmitResult;
}

export type SubmitFinish =
  | { reject: true; result?: undefined }
  | { reject: false; result: CardSubmitResult };

interface SubmitStep {
  state: SubmitState;
  effects: SubmitEffect[];
}

export const initialSubmitState = (token: string): SubmitState => ({
  token,
  started: false,
  unreachable: false,
});

const settle = (
  state: SubmitState,
  result: CardSubmitResult,
  effects: SubmitEffect[] = [],
): SubmitStep => ({
  state: { ...state, result },
  effects,
});

const unreachable = (state: SubmitState): SubmitStep => ({
  state: { ...state, unreachable: true },
  effects: [],
});

export const submitOutcome = (state: SubmitState, event: AlphaEvent): SubmitStep => {
  if (state.started) {
    return { state, effects: TURN_TYPES.includes(event.type) ? [{ type: 'forward', event }] : [] };
  }
  if (PROGRESS_TYPES.includes(event.type)) {
    return {
      state: { ...state, started: true, result: { ok: true } },
      effects: [{ type: 'closeCard' }, { type: 'startTurn' }, { type: 'forward', event }],
    };
  }
  switch (event.type) {
    case 'card_error':
      if (event.token !== state.token) return { state, effects: [{ type: 'forward', event }] };
      return settle(
        state,
        { ok: false, message: event.message, fieldErrors: event.fieldErrors ?? {} },
        [{ type: 'forward', event }],
      );
    case 'card_required':
      if (event.token === state.token) return { state, effects: [{ type: 'replaceCard', event }] };
      return settle(state, { ok: true }, [{ type: 'replaceCard', event }]);
    case 'rate_limited': {
      const given = event.message?.trim() ? event.message : RATE_LIMITED_MESSAGE;
      const reason = given.replace(/[.\s]+$/, '');
      return settle(state, { ok: false, message: `${reason}. ${RETRY_LATER}` }, [
        { type: 'rateLimited', message: event.message, retryAfterSeconds: event.retryAfterSeconds },
      ]);
    }
    case 'turn_in_progress':
      return settle(state, { ok: false, message: TURN_IN_PROGRESS_MESSAGE }, [
        { type: 'turnInProgress' },
      ]);
    case 'timeout':
      return unreachable(state);
    case 'error':
      if (event.origin !== 'client') {
        return settle(state, { ok: true }, [
          { type: 'closeCard' },
          { type: 'startTurn' },
          { type: 'forward', event },
        ]);
      }
      if (event.message === ALPHA_NO_RESPONSE_MESSAGE) {
        return settle(state, { ok: false, message: ALPHA_NO_RESPONSE_MESSAGE });
      }
      return unreachable(state);
    case 'done':
      return state.result
        ? { state, effects: [] }
        : settle(state, { ok: false, message: SUBMIT_UNFINISHED_MESSAGE });
    default:
      return { state, effects: [] };
  }
};

export const finishSubmit = (state: SubmitState, superseded: boolean): SubmitFinish => {
  if (state.started) return { reject: false, result: { ok: true } };
  if (superseded || state.unreachable) return { reject: true };
  return {
    reject: false,
    result: state.result ?? { ok: false, message: SUBMIT_UNFINISHED_MESSAGE },
  };
};
